import { useState, useEffect, useCallback } from 'react';
import statusReportsService, { StatusReportNotFoundError } from '@services/statusReports.service';
import type { LocalizedStatusReport, StatusReportSummary } from '@/types';

interface UseStatusReportIndexReturn {
  reports: StatusReportSummary[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Load the list of published status reports, newest first.
 * Pass enabled=false to skip the request (e.g. for anonymous users, whose
 * request would otherwise be redirected to the sign-in page).
 */
export function useStatusReportIndex(enabled = true): UseStatusReportIndexReturn {
  const [reports, setReports] = useState<StatusReportSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchIndex = useCallback(async (): Promise<void> => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const index = await statusReportsService.getStatusReportIndex();
      setReports(index.reports);
    } catch (err) {
      console.error('[useStatusReports] Failed to fetch status report index:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  return { reports, loading, error, refetch: fetchIndex };
}

interface UseStatusReportReturn {
  /** The report in the language it is shown in (a translation, or bokmål) */
  summary: LocalizedStatusReport | null;
  /** All published reports, newest first (for previous/next navigation) */
  reports: StatusReportSummary[];
  markdown: string;
  loading: boolean;
  notFound: boolean;
  error: string | null;
}

/**
 * Load one status report in the UI language if it has been translated,
 * otherwise in bokmål: its metadata from index.json and its markdown body.
 */
export function useStatusReport(period: string | undefined, uiLanguage: string): UseStatusReportReturn {
  const [summary, setSummary] = useState<LocalizedStatusReport | null>(null);
  const [reports, setReports] = useState<StatusReportSummary[]>([]);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      setNotFound(false);

      if (!statusReportsService.isValidPeriod(period)) {
        setSummary(null);
        setMarkdown('');
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        // The index tells which languages the report is available in
        const index = await statusReportsService.getStatusReportIndex();
        const match = index.reports.find(r => r.period === period);
        if (cancelled) return;
        if (!match) {
          // A report folder that is not in the index is a draft or unpublished
          setReports(index.reports);
          setSummary(null);
          setNotFound(true);
          return;
        }

        let localized = statusReportsService.localizeReport(match, uiLanguage);
        let body: string;
        try {
          body = await statusReportsService.getStatusReportMarkdown(period, localized.lang);
        } catch (err) {
          // Listed in the index, but the translated file was not uploaded: show bokmål
          if (!(err instanceof StatusReportNotFoundError) || localized.lang === 'nb') throw err;
          localized = { ...statusReportsService.localizeReport(match, 'nb'), isFallback: true };
          body = await statusReportsService.getStatusReportMarkdown(period, 'nb');
        }
        if (cancelled) return;
        setReports(index.reports);
        setSummary(localized);
        setMarkdown(body);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof StatusReportNotFoundError) {
          setNotFound(true);
        } else {
          console.error('[useStatusReports] Failed to fetch status report:', err);
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [period, uiLanguage]);

  return { summary, reports, markdown, loading, notFound, error };
}
